import { BufferGeometry, Color, Float32BufferAttribute, Group, LineBasicMaterial, LineSegments } from 'three';

/** Step of the thin grid line, mm. */
export const FLOOR_GRID_MINOR_STEP_MM: number = 100;

/** Step of the bold grid line, mm: every tenth line is a metre line. */
export const FLOOR_GRID_MAJOR_STEP_MM: number = 1000;

/** How far the grid runs past the room walls on every side, mm. */
export const FLOOR_GRID_MARGIN_MM: number = 2000;

/** Opacity of the thin line: it darkens the ground and the room floor alike without cutting them up. */
export const FLOOR_GRID_MINOR_OPACITY: number = 0.14;

/** Opacity of the metre line. */
export const FLOOR_GRID_MAJOR_OPACITY: number = 0.32;

/** Object names of the two line sets in the room group. */
export const FLOOR_GRID_MINOR_NAME: string = 'floor-grid-minor';
export const FLOOR_GRID_MAJOR_NAME: string = 'floor-grid-major';

/** The floor footprint the grid is laid around: the room spans 0..width along X and 0..length along Z. */
export interface IFloorGridArea {
    roomWidth: number;
    roomLength: number;
}

/**
 * Line segments of the floor grid at y = 0 as flat `x, y, z` triples, two vertices per line.
 * `minX..maxX` and `minZ..maxZ` are the grid bounds: the room plus the margin, widened outward to
 * whole metres so the metre lines pass through the room origin.
 */
export interface IFloorGridLines {
    minor: number[];
    major: number[];
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
}

/**
 * Lays the CAD-like floor grid out: a line every 100 mm along both axes over the room plus a
 * 2000 mm margin, every line on a whole metre counted from the room origin going to the bold set.
 *
 * Three's own `GridHelper` was read first and does not fit: it is a square centred on the origin
 * with one colour for the centre line, while the room is a rectangle starting at the origin and
 * needs two line weights with their own opacity.
 */
export function buildFloorGridLines(area: IFloorGridArea): IFloorGridLines {
    const minX: number = Math.floor(-FLOOR_GRID_MARGIN_MM / FLOOR_GRID_MAJOR_STEP_MM) * FLOOR_GRID_MAJOR_STEP_MM;
    const maxX: number = Math.ceil((area.roomWidth + FLOOR_GRID_MARGIN_MM) / FLOOR_GRID_MAJOR_STEP_MM) * FLOOR_GRID_MAJOR_STEP_MM;
    const minZ: number = minX;
    const maxZ: number = Math.ceil((area.roomLength + FLOOR_GRID_MARGIN_MM) / FLOOR_GRID_MAJOR_STEP_MM) * FLOOR_GRID_MAJOR_STEP_MM;

    const minor: number[] = [];
    const major: number[] = [];
    const pick: (position: number) => number[] = (position: number): number[] =>
        position % FLOOR_GRID_MAJOR_STEP_MM === 0 ? major : minor;

    // Lines are counted by index, not by adding the step, so no rounding error drifts the positions.
    const xCount: number = Math.round((maxX - minX) / FLOOR_GRID_MINOR_STEP_MM);
    for (let index: number = 0; index <= xCount; index++) {
        const x: number = minX + index * FLOOR_GRID_MINOR_STEP_MM;
        pick(x).push(x, 0, minZ, x, 0, maxZ);
    }
    const zCount: number = Math.round((maxZ - minZ) / FLOOR_GRID_MINOR_STEP_MM);
    for (let index: number = 0; index <= zCount; index++) {
        const z: number = minZ + index * FLOOR_GRID_MINOR_STEP_MM;
        pick(z).push(minX, 0, z, maxX, 0, z);
    }

    return { minor, major, minX, maxX, minZ, maxZ };
}

/**
 * The material of one grid line set. The line keeps its token colour exactly (no tone mapping),
 * blends over whatever lies under it and writes no depth; the depth test stays on, so stair wood
 * standing on the floor hides the lines behind it. The floor plane's polygon offset lets the
 * lines at y = 0 win over it — see the depth-fight table in `.claude/rules/three-js.md`.
 */
export function createFloorGridMaterial(color: string, opacity: number): LineBasicMaterial {
    return new LineBasicMaterial({
        color: new Color(color),
        transparent: true,
        opacity: opacity,
        depthWrite: false,
        toneMapped: false,
    });
}

function toSegments(positions: number[], material: LineBasicMaterial, name: string): LineSegments {
    const geometry: BufferGeometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    const segments: LineSegments = new LineSegments(geometry, material);
    segments.name = name;
    return segments;
}

/**
 * The grid as two `LineSegments` in one group. The geometry belongs to the segments and is freed
 * by the scene's group disposal; the materials are shared and freed by their owner.
 */
export function buildFloorGrid(area: IFloorGridArea, minorMaterial: LineBasicMaterial, majorMaterial: LineBasicMaterial): Group {
    const lines: IFloorGridLines = buildFloorGridLines(area);
    const group: Group = new Group();
    group.name = 'floor-grid';
    group.add(toSegments(lines.minor, minorMaterial, FLOOR_GRID_MINOR_NAME));
    group.add(toSegments(lines.major, majorMaterial, FLOOR_GRID_MAJOR_NAME));
    return group;
}
